import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  startProctoringSession,
  endProctoringSession,
  recordProctoringEvent,
  detectProctoringFrame,
} from "../api/client";

const NO_FACE_LIMIT_MS = 30000;
const DETECTION_INTERVAL_MS = 2000;

const EVENT_TYPE_MAP = {
  tab_switch: "TAB_SWITCH",
  window_blur: "TAB_SWITCH",
  copy_paste: "COPY_ATTEMPT",
  fullscreen_exit: "FULLSCREEN_EXIT",
  camera_error: "CAMERA_ERROR",
  no_face: "PERSON_ABSENT",
  multiple_faces: "MULTIPLE_PERSON",
  object_detected: "OTHER",
  camera_started: "CAMERA_STARTED",
  camera_stopped: "OTHER",
  auto_submit: "OTHER",
};

function getBackendEventType(type) {
  return EVENT_TYPE_MAP[type] || "OTHER";
}

function ProctorCamera({
  attemptId,
  attemptType = "standard",
  proctoringEnabled = true,
  onViolation,
  onAutoSubmit,
}) {
  const isBeginnerAttempt = attemptType === "beginner";

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const mountedRef = useRef(true);
  const canvasRef = useRef(null);
  const detectionBusyRef = useRef(false);
  const proctorIntervalRef = useRef(null);

  const proctoringSessionStartedRef = useRef(false);
  const proctoringStartPromiseRef = useRef(null);
  const proctoringAttemptIdRef = useRef(null);
  const endSessionTimerRef = useRef(null);
  const proctoringSessionEndedRef = useRef(false);

  const tabHiddenSinceRef = useRef(null);
  const noFaceSinceRef = useRef(null);
  const objectWarningCountRef = useRef(0);

  const mediaRecorderRef = useRef(null);
  const mediaRecorderTimerRef = useRef(null);
  const recordingStoppingRef = useRef(false);

  const autoSubmittedRef = useRef(false);
  const autoSubmitTimerRef = useRef(null);

  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [warning, setWarning] = useState("");

  /*
   * -------------------------------------------------------------
   * RECORD BACKEND PROCTORING EVENT
   * -------------------------------------------------------------
   */
  const logEvent = useCallback(
    async (type, detail = "", severity = "WARNING") => {
      if (!attemptId || proctoringSessionEndedRef.current) return;
      const eventType = getBackendEventType(type);
      try {
        await recordProctoringEvent(
          attemptId,
          {
            event_type: eventType,
            severity,
            detail: detail || "",
            occurred_at: new Date().toISOString(),
          },
          isBeginnerAttempt
        );
      } catch (error) {
        console.error("Proctoring event failed:", error);
      }
    },
    [attemptId, isBeginnerAttempt]
  );

  /*
   * -------------------------------------------------------------
   * WARNING
   * -------------------------------------------------------------
   */
  const showViolation = useCallback(
    async (type, detail) => {
      setWarning(detail);
      if (onViolation) {
        onViolation({ type, detail });
      }
      await logEvent(type, detail, "WARNING");
      window.setTimeout(() => {
        if (mountedRef.current) {
          setWarning("");
        }
      }, 3000);
    },
    [logEvent, onViolation]
  );

  /*
   * -------------------------------------------------------------
   * AUTO SUBMIT
   * -------------------------------------------------------------
   */
  const triggerAutoSubmit = useCallback(
    (reason) => {
      if (autoSubmittedRef.current || autoSubmitTimerRef.current) {
        return;
      }
      const submitReason = reason || "Proctoring violation detected.";
      setWarning(
        `${submitReason} The examination will be submitted automatically.`
      );

      if (onViolation) {
        onViolation({
          type: "auto_submit",
          detail: submitReason,
        });
      }

      autoSubmitTimerRef.current = window.setTimeout(async () => {
        autoSubmitTimerRef.current = null;
        if (autoSubmittedRef.current) return;
        autoSubmittedRef.current = true;

        await logEvent("auto_submit", submitReason, "CRITICAL");

        // Flush recorded video chunk on auto submit
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state === "recording"
        ) {
          try {
            mediaRecorderRef.current.stop();
          } catch (err) {
            console.warn("MediaRecorder stop on auto-submit failed:", err);
          }
        }

        if (onAutoSubmit) {
          onAutoSubmit(submitReason);
        }
      }, 3000);
    },
    [logEvent, onAutoSubmit, onViolation]
  );

  /*
   * -------------------------------------------------------------
   * PROCTORING VIDEO RECORDING & UPLOAD
   * -------------------------------------------------------------
   */
  const getProctorAccessToken = () => {
    return (
      localStorage.getItem("access_token") ||
      localStorage.getItem("oes_access_token") ||
      ""
    );
  };

  const detectFrameDirect = useCallback(
    async (blob) => {
      if (!attemptId || !blob || proctoringSessionEndedRef.current) {
        return null;
      }
      try {
        const response = await detectProctoringFrame(
          attemptId,
          blob,
          isBeginnerAttempt
        );
        return response?.data || null;
      } catch (error) {
        console.error(
          "YOLO DETECTION REQUEST FAILED:",
          error?.response?.status,
          error?.response?.data || error?.message
        );
        throw error;
      }
    },
    [attemptId, isBeginnerAttempt]
  );

  const uploadVideoSegment = useCallback(
    async (blob, startedAt, endedAt) => {
      if (!attemptId || !blob || blob.size === 0) return;
      const baseURL =
        process.env.REACT_APP_DJANGO_URL ||
        process.env.REACT_APP_API_URL ||
        "https://mockq.wahylab.com";

      const apiBase = baseURL.replace(/\/+$/, "").replace(/\/api$/, "");
      const attemptRoute = isBeginnerAttempt
        ? "beginner-attempts"
        : "attempts";
      const url = `${apiBase}/api/proctoring/${attemptRoute}/${attemptId}/video/`;

      const formData = new FormData();
      formData.append(
        "video",
        blob,
        `proctoring-${attemptId}-${Date.now()}.webm`
      );
      formData.append("started_at", startedAt);
      formData.append("ended_at", endedAt);

      const sendUpload = async (token) => {
        return fetch(url, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        });
      };

      try {
        let token = getProctorAccessToken();
        let response = await sendUpload(token);

        if (response.status === 401) {
          const refresh =
            localStorage.getItem("refresh_token") ||
            localStorage.getItem("oes_refresh_token");
          if (refresh) {
            const refreshResponse = await fetch(
              `${apiBase}/api/auth/token/refresh/`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ refresh }),
              }
            );

            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json();
              token = refreshData.access || token;
              if (token) {
                localStorage.setItem("access_token", token);
                localStorage.setItem("oes_access_token", token);
              }
              response = await sendUpload(token);
            }
          }
        }

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `Video upload failed (${response.status}): ${errorText}`
          );
        }

        const result = await response.json().catch(() => null);
        console.log("PROCTOR VIDEO UPLOADED", {
          attemptId,
          sizeKB: Math.round(blob.size / 1024),
          response: result,
        });
      } catch (error) {
        console.error("PROCTOR VIDEO UPLOAD FAILED:", error);
      }
    },
    [attemptId, isBeginnerAttempt]
  );

  const startRecordingSegment = useCallback(() => {
    const stream = streamRef.current;
    if (!stream || typeof MediaRecorder === "undefined") {
      console.error("PROCTOR VIDEO: MediaRecorder is not available.");
      return;
    }

    const supportedTypes = [
      "video/webm;codecs=vp8",
      "video/webm;codecs=vp9",
      "video/webm",
    ];
    const mimeType = supportedTypes.find((type) =>
      MediaRecorder.isTypeSupported(type)
    );

    try {
      recordingStoppingRef.current = false;
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      const chunks = [];
      const startedAt = new Date().toISOString();

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const blob = new Blob(chunks, {
          type: recorder.mimeType || "video/webm",
        });

        await uploadVideoSegment(
          blob,
          startedAt,
          new Date().toISOString()
        );
        chunks.length = 0;
        if (mediaRecorderRef.current === recorder) {
          mediaRecorderRef.current = null;
        }
      };

      recorder.start(1000);
      mediaRecorderRef.current = recorder;
    } catch (error) {
      console.error("PROCTOR VIDEO START FAILED:", error);
    }
  }, [uploadVideoSegment]);

  /*
   * -------------------------------------------------------------
   * START CAMERA + DJANGO PROCTORING SESSION
   * -------------------------------------------------------------
   */
  useEffect(() => {
    mountedRef.current = true;
    if (!attemptId || !proctoringEnabled) return undefined;

    const previousAttemptId = proctoringAttemptIdRef.current;
    if (
      endSessionTimerRef.current &&
      endSessionTimerRef.current.attemptId === attemptId
    ) {
      window.clearTimeout(endSessionTimerRef.current.timer);
      endSessionTimerRef.current = null;
    }

    if (previousAttemptId !== attemptId) {
      proctoringAttemptIdRef.current = attemptId;
      proctoringStartPromiseRef.current = null;
      proctoringSessionStartedRef.current = false;
      proctoringSessionEndedRef.current = false;
      autoSubmittedRef.current = false;
    }

    let cancelled = false;

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: "user",
          },
          audio: false,
        });

        if (cancelled || !mountedRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try {
            await videoRef.current.play();
          } catch (playError) {
            console.warn("Camera video play warning:", playError);
          }
        }

        if (mountedRef.current) {
          setCameraReady(true);
          setCameraError("");
          startRecordingSegment();
        }

        if (!proctoringStartPromiseRef.current) {
          proctoringStartPromiseRef.current = startProctoringSession(
            attemptId,
            isBeginnerAttempt
          );
        }

        proctoringStartPromiseRef.current
          .then(async () => {
            if (cancelled || !mountedRef.current) return;
            if (!proctoringSessionStartedRef.current) {
              proctoringSessionStartedRef.current = true;
              await logEvent(
                "camera_started",
                "Proctoring session started.",
                "INFO"
              );
            }
          })
          .catch((sessionError) => {
            console.error(
              "Django proctoring session failed:",
              sessionError
            );
          });
      } catch (error) {
        if (proctoringAttemptIdRef.current === attemptId) {
          proctoringStartPromiseRef.current = null;
        }
        if (cancelled || !mountedRef.current) return;
        setCameraError(
          error?.response?.data?.detail ||
            error?.message ||
            "Camera permission is required for this examination."
        );
        await logEvent(
          "camera_error",
          error?.message || "Unable to start camera.",
          "CRITICAL"
        );
      }
    };

    start();

    return () => {
      cancelled = true;
      mountedRef.current = false;

      // 1. Clear frame processing timers
      if (proctorIntervalRef.current) {
        window.clearTimeout(proctorIntervalRef.current);
        proctorIntervalRef.current = null;
      }

      // 2. Stop camera tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      // 3. Stop MediaRecorder synchronously to trigger uploadVideoSegment
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === "recording"
      ) {
        try {
          mediaRecorderRef.current.stop();
        } catch (error) {
          console.warn("PROCTOR VIDEO STOP FAILED:", error);
        }
      }
      mediaRecorderRef.current = null;

      // 4. Send endProctoringSession request after 500ms delay to allow video upload HTTP POST to start
      if (proctoringSessionStartedRef.current) {
        const activeAttemptId = attemptId;
        const activeIsBeginner = isBeginnerAttempt;
        window.setTimeout(() => {
          proctoringSessionEndedRef.current = true;
          endProctoringSession(activeAttemptId, activeIsBeginner).catch(
            (error) => {
              console.warn("Unable to end proctoring session:", error);
            }
          );
        }, 500);
      }
    };
  }, [
    attemptId,
    attemptType,
    isBeginnerAttempt,
    proctoringEnabled,
    logEvent,
    startRecordingSegment,
  ]);

  /* -------------------------------------------------------------
   * TAB SWITCH / WINDOW BLUR
   * ------------------------------------------------------------- */
  useEffect(() => {
    if (!attemptId || !proctoringEnabled) return undefined;

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        const reason =
          "You switched away from the examination tab or window. Please return to the examination.";
        showViolation("tab_switch", reason);
        triggerAutoSubmit(reason);
      }
    };

    const handleWindowBlur = () => {
      const reason =
        "The examination window lost focus. Please remain on the examination window.";
      showViolation("window_blur", reason);
      triggerAutoSubmit(reason);
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [attemptId, proctoringEnabled, showViolation, triggerAutoSubmit]);

  /*
   * -------------------------------------------------------------
   * YOLO BACKEND DETECTION
   * -------------------------------------------------------------
   */
  useEffect(() => {
    if (!attemptId || !proctoringEnabled || !cameraReady) {
      return undefined;
    }

    let cancelled = false;

    const scheduleNext = () => {
      if (cancelled || proctoringSessionEndedRef.current) return;
      proctorIntervalRef.current = window.setTimeout(
        runDetection,
        DETECTION_INTERVAL_MS
      );
    };

    const runDetection = async () => {
      if (
        detectionBusyRef.current ||
        cancelled ||
        proctoringSessionEndedRef.current
      ) {
        scheduleNext();
        return;
      }

      const video = videoRef.current;
      if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        scheduleNext();
        return;
      }

      const stream = streamRef.current;
      const videoTrack = stream?.getVideoTracks?.()[0];

      if (!videoTrack || videoTrack.readyState !== "live") {
        scheduleNext();
        return;
      }

      if (!canvasRef.current) {
        canvasRef.current = document.createElement("canvas");
      }

      const canvas = canvasRef.current;
      const width = video.videoWidth || 640;
      const height = video.videoHeight || 480;
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext("2d");
      if (!context) {
        scheduleNext();
        return;
      }

      context.drawImage(video, 0, 0, width, height);
      detectionBusyRef.current = true;

      try {
        const blob = await new Promise((resolve) => {
          canvas.toBlob(resolve, "image/jpeg", 0.75);
        });

        if (!blob || proctoringSessionEndedRef.current) return;

        const data = await detectFrameDirect(blob);
        if (!data) return;

        const monitor = data.monitor || {};
        const backendRequestsAutoSubmit =
          data.auto_submit === true || monitor.auto_submit === true;

        if (backendRequestsAutoSubmit) {
          triggerAutoSubmit(
            "The proctoring system reached the violation limit. Your examination will be submitted automatically."
          );
        }
      } catch (error) {
        console.error("YOLO proctoring detection failed:", error);
      } finally {
        detectionBusyRef.current = false;
        scheduleNext();
      }
    };

    runDetection();

    return () => {
      cancelled = true;
      if (proctorIntervalRef.current) {
        window.clearTimeout(proctorIntervalRef.current);
        proctorIntervalRef.current = null;
      }
      detectionBusyRef.current = false;
    };
  }, [
    attemptId,
    proctoringEnabled,
    cameraReady,
    detectFrameDirect,
    triggerAutoSubmit,
  ]);

  useEffect(() => {
    return () => {
      if (autoSubmitTimerRef.current) {
        window.clearTimeout(autoSubmitTimerRef.current);
        autoSubmitTimerRef.current = null;
      }
    };
  }, []);

  return (
    <>
      <div
        style={{
          position: "fixed",
          right: 20,
          bottom: 20,
          width: 210,
          zIndex: 2000,
          background: "#111827",
          borderRadius: 12,
          padding: 8,
          boxShadow: "0 8px 30px rgba(0,0,0,0.25)",
        }}
      >
        <canvas ref={canvasRef} style={{ display: "none" }} />
        <video
          ref={videoRef}
          muted
          playsInline
          autoPlay
          style={{
            display: "block",
            width: "100%",
            height: 150,
            objectFit: "cover",
            borderRadius: 8,
            background: "#000",
            transform: "scaleX(-1)",
          }}
        />
        <div className="small text-white mt-2 d-flex align-items-center justify-content-between">
          <span>
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: cameraReady ? "#22c55e" : "#ef4444",
                marginRight: 6,
              }}
            />
            {cameraReady ? "Camera Active" : "Starting Camera..."}
          </span>
        </div>
        {cameraError && (
          <div className="small text-warning mt-2">{cameraError}</div>
        )}
      </div>

      {warning && (
        <div
          style={{
            position: "fixed",
            top: 20,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 3000,
            maxWidth: 500,
            width: "calc(100% - 32px)",
          }}
        >
          <div className="alert alert-warning shadow mb-0">
            <strong>Proctoring Warning</strong>
            <div className="mt-1">{warning}</div>
          </div>
        </div>
      )}
    </>
  );
}

export { ProctorCamera };
export default ProctorCamera;